 
import { Dispatch } from 'redux'
import { api, apiErrorHandler, getApiRequestConfig, Response } from '../../api'
import { GetState } from '../reducers'
import { Order } from '../reducers/order.reducer'
import {OrderReport} from '../reducers/orderReport.reducer'

export enum OrderActionTypes {
    ADD_ORDER = 'ADD_ORDER',
    FETCH_ORDERS = 'FETCH_ORDERS',
    REPLACE_ORDER = 'REPLACE_ORDER',
    FETCH_ORDER_REPORTS = 'FETCH_ORDER_REPORTS'
}

export interface AddOrderAction {
    type: OrderActionTypes.ADD_ORDER
    data: Order
}

export interface FetchOrdersAction {
    type: OrderActionTypes.FETCH_ORDERS
    data: Order[]
}

export interface FetchOrderReportAction {
    type: OrderActionTypes.FETCH_ORDER_REPORTS
    data: OrderReport[]
}

export interface ReplaceOrderAction {
    type: OrderActionTypes.REPLACE_ORDER
    data: Order
}

export type OrderActions = AddOrderAction | FetchOrdersAction | ReplaceOrderAction | FetchOrderReportAction

export interface OrderDetailReport {
    id: string
    orderNumber: string
    orderDate: string
    buyer: {
        firstName: string
        lastName: string
    }
    billAmount: number
    totalTax: number | null
    serviceCharge: number | null
    deliveryCharge: number | null
}

export const addOrder = (input: Order): AddOrderAction => {
    return {
        type: OrderActionTypes.ADD_ORDER,
        data: input
    }
}

export const updateOrder = (input: Order): ReplaceOrderAction => {
    return {
        type: OrderActionTypes.REPLACE_ORDER,
        data: input
    }
}


export const fetchShopOrders = () => async (dispatch: Dispatch, getState: GetState) => {
    const token = getState().authAdmin?.['auth-token']
    const config = getApiRequestConfig(token)

    return api.get<Response<Order[]>>('shop/orders', config).then(response => {
        if (response.status === 200) {
            dispatch<FetchOrdersAction>({
                type: OrderActionTypes.FETCH_ORDERS,
                data: response.data.data
            })

            return Promise.resolve<string>(response.data.message || '')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to fetch'))
    })
}

export const fetchShopOrderReports = (fromDate:string,toDate:string) => async (dispatch: Dispatch, getState: GetState) => {
    const token = getState().authAdmin?.['auth-token']
    const config = getApiRequestConfig(token)

    return api.get<Response<OrderReport[]>>(`shop/order-reports/${fromDate}/${toDate}`, config).then(response => {
        if (response.status === 200) {
            dispatch<FetchOrderReportAction>({
                type: OrderActionTypes.FETCH_ORDER_REPORTS,
                data: response.data.data
            })

            return Promise.resolve<string>(response.data.message || '')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to fetch'))
    })
}

export const fetchShopOrderDetailReports = (fromDate: string, toDate: string) => async (_dispatch: Dispatch, getState: GetState) => {
    const token = getState().authAdmin?.['auth-token']
    const config = getApiRequestConfig(token)

    return api.get<Response<OrderDetailReport[]>>(`shop/order-detail-reports/${fromDate}/${toDate}`, config).then(response => {
        if (response.status === 200) {
            return Promise.resolve<OrderDetailReport[]>(response.data.data ?? [])
        }

        throw { response }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to fetch'))
    })
}

export const markOrderReady = (orderId: string) => async (dispatch: Dispatch, getState: GetState) => {
    const token = getState().authAdmin?.['auth-token']
    const config = getApiRequestConfig(token)

    return api.put<Response<Order>>(`order/mark-ready/${orderId}`, {}, config).then(response => {
        if (response.status === 200) {
            dispatch<ReplaceOrderAction>({
                type: OrderActionTypes.REPLACE_ORDER,
                data: response.data.data
            })

            return Promise.resolve<string>(response.data.message || 'Order marked as ready')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to mark order as ready'))
    })
}

export const startOrderProcessing = (orderId: string) => async (dispatch: Dispatch, getState: GetState) => {
    const token = getState().authAdmin?.['auth-token']
    const config = getApiRequestConfig(token)

    return api.put<Response<Order>>(`order/start-processing/${orderId}`, {}, config).then(response => {
        if (response.status === 200) {
            dispatch<ReplaceOrderAction>({
                type: OrderActionTypes.REPLACE_ORDER,
                data: response.data.data
            })

            return Promise.resolve<string>(response.data.message || 'Order processing started')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to start processing'))
    })
}

export const acceptPreOrder = (orderId: string) => async (dispatch: Dispatch, getState: GetState) => {
    const token = getState().authAdmin?.['auth-token']
    const config = getApiRequestConfig(token)

    return api.put<Response<Order>>(`order/accept-pre-order/${orderId}`, {}, config).then(response => {
        if (response.status === 200) {
            dispatch<ReplaceOrderAction>({
                type: OrderActionTypes.REPLACE_ORDER,
                data: response.data.data
            })

            return Promise.resolve<string>(response.data.message || 'Pre-order accepted')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to accept pre-order'))
    })
}

export const setOrderProcessTime = (processTime: number, orderId: string) => async (dispatch: Dispatch, getState: GetState) => {
    const token = getState().authAdmin?.['auth-token']
    const config = getApiRequestConfig(token)

    return api.put<Response<Order>>(`order/set-process-time/${orderId}`, { processTime }, config).then(response => {
        if (response.status === 200) {
            dispatch<ReplaceOrderAction>({
                type: OrderActionTypes.REPLACE_ORDER,
                data: response.data.data
            })

            return Promise.resolve<string>(response.data.message || 'Process time setted')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to set process time'))
    })
}

export const updateOrderStatus = (paymentStatus: string, orderId: string) => async (dispatch: Dispatch, getState: GetState) => {
    const token = getState().authAdmin?.['auth-token']
    const config = getApiRequestConfig(token)

    return api.put<Response<Order>>(`order/verify/payment-status/${orderId}`, { paymentStatus }, config).then(response => {
        if (response.status === 200) {
            dispatch<ReplaceOrderAction>({
                type: OrderActionTypes.REPLACE_ORDER,
                data: response.data.data
            })

            return Promise.resolve<string>(response.data.message || 'Process time setted')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to set process time'))
    })
}
export const updateOrderStatusForOrder = (orderStatus: string, orderId: string) => async (dispatch: Dispatch, getState: GetState) => {
    const token = getState().authAdmin?.['auth-token']
    const config = getApiRequestConfig(token)

    return api.put<Response<Order>>(`order/order-status/${orderId}`, { orderStatus }, config).then(response => {
        if (response.status === 200) {
            dispatch<ReplaceOrderAction>({
                type: OrderActionTypes.REPLACE_ORDER,
                data: response.data.data
            })

            return Promise.resolve<string>(response.data.message || 'OrderStatus updated')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to update order status '))
    })
}